import React, { useState, useEffect } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import toast from 'react-hot-toast';
import {
  ArrowLeftIcon,
  GlobeAltIcon,
  UserGroupIcon,
  InformationCircleIcon,
  ArrowTopRightOnSquareIcon,
  TagIcon,
  EyeIcon,
  EyeSlashIcon,
  UserPlusIcon,
  ArrowsRightLeftIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import AddTeamMembersModal from '../components/AddTeamMembersModal';
import TransferOwnershipModal from '../components/TransferOwnershipModal';

const ApplicationDetail = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(true);
  const [visiblePasswords, setVisiblePasswords] = useState({});
  const [showAddMembersModal, setShowAddMembersModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);

  const location = useLocation();

  const getBackLink = () => {
    if (location.state?.from === 'bug-report') return '/bugs/new';
    if (location.state?.from === 'tester-applications') return '/tester/applications';
    if (user?.role === 'tester') return '/tester/applications';
    return '/applications';
  };

  const backLink = getBackLink();
  const backText = location.state?.from === 'bug-report' ? 'Back to Bug Report' : 'Back to Applications';

  useEffect(() => {
    fetchApplication();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchApplication = async () => {
    try {
      const { data } = await api.get(`/api/applications/${id}`);
      setApplication(data.data);
      setLoading(false);
    } catch (error) {
      toast.error('Failed to fetch application details');
      setLoading(false);
    }
  };

  const handleRemoveMember = async (memberId) => {
    if (!application?._id || !memberId) return;
    const confirmed = window.confirm('Are you sure you want to remove this team member from the application?');
    if (!confirmed) return;
    try {
      await api.delete(`/api/applications/${application._id}/team-members/${memberId}`);
      toast.success('Team member removed successfully');
      fetchApplication();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to remove team member');
    }
  };

  const togglePasswordVisibility = (userIndex) => {
    setVisiblePasswords(prev => ({
      ...prev,
      [userIndex]: !prev[userIndex]
    }));
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!application) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900">Application not found</h2>
            <p className="mt-2 text-gray-600">The application you're looking for doesn't exist.</p>
            <Link
              to={backLink}
              className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200"
            >
              <ArrowLeftIcon className="h-4 w-4 mr-2" />
              Back to Applications
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <Link
            to={backLink}
            className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-1" />
            {backText}
          </Link>

          <div className="flex items-center justify-between">
            <div>
              <Link
                to={`/applications/${application._id}/timeline`}
                className="text-3xl font-bold text-gray-900 hover:text-indigo-600 hover:underline cursor-pointer block"
              >
                {application.name}
              </Link>
              <p className="mt-1 text-sm text-gray-600">
                Application Details for Testing
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${application.status === 'Active'
                ? 'bg-green-100 text-green-800'
                : application.status === 'Inactive'
                  ? 'bg-yellow-100 text-yellow-800'
                  : 'bg-red-100 text-red-800'
                }`}>
                {application.status}
              </span>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Application Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Information */}
            <div className="bg-white shadow rounded-lg p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <InformationCircleIcon className="h-5 w-5 mr-2 text-gray-400" />
                Application Information
              </h2>

              <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Application Name</dt>
                  <dd className="mt-1 text-sm text-gray-900">{application.name}</dd>
                </div>

                <div>
                  <dt className="text-sm font-medium text-gray-500">Version</dt>
                  <dd className="mt-1 text-sm text-gray-900 flex items-center">
                    <TagIcon className="h-4 w-4 mr-1 text-gray-400" />
                    {application.version}
                  </dd>
                </div>

                <div>
                  <dt className="text-sm font-medium text-gray-500">Status</dt>
                  <dd className="mt-1">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${application.status === 'Active'
                      ? 'bg-green-100 text-green-800'
                      : application.status === 'Inactive'
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-red-100 text-red-800'
                      }`}>
                      {application.status}
                    </span>
                  </dd>
                </div>

                <div>
                  <dt className="text-sm font-medium text-gray-500">Developer</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {application.developerId?.name || 'Unknown'}
                  </dd>
                </div>
              </dl>

              {application.description && (
                <div className="mt-6">
                  <dt className="text-sm font-medium text-gray-500">Description</dt>
                  <dd className="mt-2 text-sm text-gray-900 bg-gray-50 p-3 rounded-md">
                    {application.description}
                  </dd>
                </div>
              )}
            </div>

            {/* Team Members remain in left column for non-developers only */}
            {user?.role !== 'developer' && application.teamMembers && application.teamMembers.length > 0 && (
              <div className="bg-white shadow rounded-lg p-6">
                <h2 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                  <UserGroupIcon className="h-5 w-5 mr-2 text-gray-400" />
                  Team Members
                </h2>
                <div className="space-y-2">
                  {application.teamMembers.map((member, index) => {
                    const memberUser = member.userId || member;
                    const ownerId = application.ownerId?._id || application.ownerId;
                    const isAppOwner = memberUser._id === ownerId || memberUser === ownerId;
                    const memberName = member.userId?.name || memberUser.name || 'Unknown';
                    return (
                      <div key={memberUser._id || index} className="flex items-center justify-between p-2 bg-gray-50 rounded-md">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-gray-900">
                            {memberName}
                          </span>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${member.role === 'QA' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'}`}>
                            {member.role}
                          </span>
                        </div>
                        {isAppOwner && (
                          <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                            Owner
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Application Access */}
            <div className="bg-white shadow rounded-lg p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <GlobeAltIcon className="h-5 w-5 mr-2 text-gray-400" />
                Application Access
              </h2>

              <div className="space-y-4">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Application URL</dt>
                  <dd className="mt-1">
                    <a
                      href={application.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-sm text-indigo-600 hover:text-indigo-800"
                    >
                      <ArrowTopRightOnSquareIcon className="h-4 w-4 mr-1" />
                      {application.url}
                    </a>
                  </dd>
                </div>

                {application.modules && application.modules.length > 0 && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500 mb-2">Available Modules</dt>
                    <dd className="flex flex-wrap gap-2">
                      {application.modules.map((module, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                        >
                          {module}
                        </span>
                      ))}
                    </dd>
                  </div>
                )}
              </div>
            </div>

            {/* Application Domains */}
            {application.domains && application.domains.length > 0 && (
              <div className="bg-white shadow rounded-lg p-6">
                <h2 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                  <GlobeAltIcon className="h-5 w-5 mr-2 text-gray-400" />
                  Application Domains
                </h2>

                <div className="space-y-6">
                  {application.domains.map((domain, index) => (
                    <div key={index} className="bg-gray-50 border border-gray-200 p-4 rounded-lg">
                      <div className="flex items-center justify-between mb-3 pb-2 border-b border-gray-200">
                        <div className="flex items-center space-x-2">
                          <h3 className="text-md font-semibold text-gray-900">{domain.name}</h3>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium ${domain.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                            }`}>
                            {domain.status}
                          </span>
                        </div>
                      </div>

                      {domain.customFields && domain.customFields.length > 0 ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-3">
                          {domain.customFields.map((field, fIdx) => (
                            <div key={fIdx} className="bg-white px-3 py-2 rounded border border-gray-100 shadow-sm">
                              <div className="flex items-center justify-between mb-1">
                                <dt className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">{field.name}</dt>
                                <span className="text-[10px] bg-gray-100 px-1 rounded text-gray-600 font-mono">{field.type}</span>
                                {field.isSensitive && <span className="text-[10px] bg-yellow-100 text-yellow-800 px-1 rounded">M</span>}
                              </div>
                              <dd className="text-sm font-mono text-gray-900 break-all">
                                {field.value}
                              </dd>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-xs text-gray-500 italic">No custom fields configured for this domain.</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Test Credentials */}
            {application.users && application.users.length > 0 && (
              <div className="bg-white shadow rounded-lg p-6">
                <h2 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                  <UserGroupIcon className="h-5 w-5 mr-2 text-gray-400" />
                  Test Credentials
                </h2>

                <div className="space-y-3">
                  {application.users.map((user, index) => (
                    <div key={index} className="bg-gray-50 border border-gray-200 p-4 rounded-lg">
                      {/* Row 1: Username/Email (full width) */}
                      <div className="mb-3">
                        <dt className="text-xs font-medium text-gray-500">Username/Email</dt>
                        <dd className="mt-1 text-sm font-mono bg-white px-2 py-1 rounded border w-full"
                          style={{ wordBreak: 'break-all', whiteSpace: 'normal' }}>
                          {user.username}
                        </dd>
                      </div>
                      {/* Row 2: Password + Role */}
                      <div className="flex items-start gap-3">
                        <div className="flex-1 min-w-0">
                          <dt className="text-xs font-medium text-gray-500">Password</dt>
                          <dd className="mt-1 flex items-center min-w-0">
                            <span className="text-sm font-mono bg-white px-2 py-1 rounded border flex-1 min-w-0 mr-2"
                              style={{ wordBreak: 'break-all', whiteSpace: 'normal' }}>
                              {visiblePasswords[index] ? user.password : '••••••••'}
                            </span>
                            <button
                              type="button"
                              onClick={() => togglePasswordVisibility(index)}
                              className="flex-shrink-0 p-1 text-gray-400 hover:text-gray-600 focus:outline-none"
                              title={visiblePasswords[index] ? 'Hide password' : 'Show password'}
                            >
                              {visiblePasswords[index] ? (
                                <EyeSlashIcon className="h-4 w-4" />
                              ) : (
                                <EyeIcon className="h-4 w-4" />
                              )}
                            </button>
                          </dd>
                        </div>
                        <div className="flex-shrink-0">
                          <dt className="text-xs font-medium text-gray-500">Role</dt>
                          <dd className="mt-1">
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800">
                              {user.role}
                            </span>
                          </dd>
                        </div>
                      </div>
                      {user.description && (
                        <div className="mt-2">
                          <dt className="text-xs font-medium text-gray-500">Description</dt>
                          <dd className="mt-1 text-xs text-gray-600">{user.description}</dd>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Actions - hidden for developers */}
            {user?.role !== 'developer' && (
              <div className="bg-white shadow rounded-lg p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Quick Actions</h3>
                <div className="space-y-3">
                  <a
                    href={application.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
                  >
                    <ArrowTopRightOnSquareIcon className="h-4 w-4 mr-2" />
                    Open Application
                  </a>
                  {(() => {
                    const reportBugPath = user?.role === 'developer'
                      ? `/developer/report-bug?application=${application._id}`
                      : `/bugs/new?application=${application._id}`;
                    return (
                      <Link
                        to={reportBugPath}
                        state={{ selectedApplication: application._id }}
                        className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 shadow-sm transition-colors"
                      >
                        Report Bug for this App
                      </Link>
                    );
                  })()}
                </div>
              </div>
            )}

            {/* Team Members (moved to right for developers) */}
            {user?.role === 'developer' && application.teamMembers && application.teamMembers.length > 0 && (
              <div className="bg-white shadow rounded-lg p-6">
                <div className="mb-3">
                  <div className="flex items-center gap-2">
                    <h2 className="text-lg font-medium text-gray-900 flex items-center">
                      <UserGroupIcon className="h-5 w-5 mr-2 text-gray-400" />
                      Team Members
                    </h2>
                  </div>
                  <div className="mt-1 flex items-center gap-4 flex-wrap">
                    <button
                      onClick={() => setShowTransferModal(true)}
                      className="inline-flex items-center gap-1 text-sm font-semibold text-red-600 hover:text-red-700"
                    >
                      <ArrowsRightLeftIcon className="h-4 w-4" />
                      Transfer
                    </button>
                    <button
                      onClick={() => setShowAddMembersModal(true)}
                      className="inline-flex items-center gap-1 text-sm font-semibold text-blue-600 hover:text-blue-700"
                    >
                      <UserPlusIcon className="h-4 w-4" />
                      Add Members
                    </button>
                  </div>
                </div>
                <div className="space-y-2">
                  {(() => {
                    const ownerId = application.ownerId?._id || application.ownerId;
                    const isCurrentUserOwner = user?._id && (user._id === ownerId);
                    return application.teamMembers.map((member, index) => {
                      const memberUser = member.userId || member;
                      const isAppOwner = memberUser._id === ownerId || memberUser === ownerId;
                      const memberName = member.userId?.name || memberUser.name || 'Unknown';
                      const memberId = member.userId?._id || memberUser._id || memberUser;
                      return (
                        <div key={memberUser._id || index} className="flex items-center justify-between p-2 bg-gray-50 rounded-md">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-gray-900">
                              {memberName}
                            </span>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${member.role === 'QA' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'}`}>
                              {member.role}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            {isAppOwner && (
                              <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                                Owner
                              </span>
                            )}
                            {isCurrentUserOwner && !isAppOwner && (
                              <button
                                onClick={() => handleRemoveMember(memberId)}
                                className="p-1 text-gray-500 hover:text-red-600"
                                title="Remove member"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>
            )}

            {/* Testing Tips */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <h3 className="text-sm font-medium text-blue-900 mb-2">Testing Tips</h3>
              <ul className="text-xs text-blue-800 space-y-1">
                <li>• Test with different user roles if available</li>
                <li>• Check all modules and features</li>
                <li>• Test on different browsers and devices</li>
                <li>• Document any issues with clear steps to reproduce</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <AddTeamMembersModal
        isOpen={showAddMembersModal}
        onClose={() => setShowAddMembersModal(false)}
        applicationId={application._id}
        onSuccess={() => {
          setShowAddMembersModal(false);
          fetchApplication();
        }}
      />
      <TransferOwnershipModal
        isOpen={showTransferModal}
        onClose={() => setShowTransferModal(false)}
        applicationId={application._id}
        teamMembers={application.teamMembers || []}
        currentOwnerId={application.ownerId?._id || application.ownerId}
        onSuccess={() => {
          setShowTransferModal(false);
          fetchApplication();
        }}
      />
    </div>
  );
};

export default ApplicationDetail;
